import { DataTable, TablePagination } from '@/components/ui/data-table'
import { cn } from '@/lib/utils'
import { ColumnDef, RowSelectionState } from '@tanstack/react-table'
import Card from 'app/frontend/base/components/Card'
import CharacterRoleIcon from 'app/frontend/base/components/CharacterRoleIcon'
import BannerImage from 'app/frontend/base/components/BannerImage'
import { presentParticipantStatuses } from 'app/frontend/types/custom/EventParticipantStatus'
import dayjs from 'dayjs'
import RelativeTime from 'dayjs/plugin/relativeTime'
import { Loader2 } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import EventParticipantStatusControl from './EventParticipantStatusControl'
import { participantColor } from '../utils/participantColor'
import {
  useEventRaidReports,
  usePageInfo,
  useTeamEvent,
  useTeamEventRaidReportsMap,
} from 'app/frontend/base/config/queries'
import EventParticipantEditPopover from '../../TeamEvent/components/EventParticipantEditPopover'
import EventRaidReportsDialog from '../../TeamEvent/components/EventRaidReportsDialog'
import {
  eventDateColumn,
  eventRaidReportsColumn,
  eventTimeColumn,
  eventTitleColumn,
} from '../../column-definitions/events'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'

dayjs.extend(RelativeTime)

const PlainWrapper = ({ children }: { children?: React.ReactNode }) => (
  <div>{children}</div>
)

type EventListTableProps = {
  userId?: number
  events: wowaudit.EventReference[]
  page: number
  totalPages: number
  isLoadingEvents: boolean
  isUpdatingStatus: boolean
  updatingEventId?: number
  onPageChange: (nextPage: number) => void
  onRowClick: (event: wowaudit.EventReference) => void
  membershipForEvent: (
    event: wowaudit.EventReference,
  ) => wowaudit.TeamMembership | undefined
  selectableStatusesForEvent: (
    event: wowaudit.EventReference,
  ) => wowaudit.EventParticipantReference['status'][]
  onSelectStatus: (input: {
    event: wowaudit.EventReference
    participant?: wowaudit.EventParticipantReference
    membership?: wowaudit.TeamMembership
    nextStatus: wowaudit.EventParticipantReference['status']
  }) => void
  onSaveWithComment?: (input: {
    event: wowaudit.EventReference
    participant: wowaudit.EventParticipantReference
    membership?: wowaudit.TeamMembership
    nextStatus: wowaudit.EventParticipantReference['status']
    comment: string
  }) => void
  canAdminEditEvent?: (event: wowaudit.EventReference) => boolean
  allowParticipantSelfEdit?: boolean
  team?: wowaudit.Team
  isBulkUpdating?: boolean
  bulkSelectableStatuses?: wowaudit.EventParticipantReference['status'][]
  onBulkSelectStatus?: (
    eventIds: number[],
    nextStatus: wowaudit.EventParticipantReference['status'],
    comment?: string,
  ) => void
  tab?: 'upcoming' | 'past'
  onTabChange?: (nextTab: 'upcoming' | 'past') => void
}

const EventListTable = ({
  userId,
  events,
  page,
  totalPages,
  isLoadingEvents,
  isUpdatingStatus,
  updatingEventId,
  onPageChange,
  onRowClick,
  membershipForEvent,
  selectableStatusesForEvent,
  onSelectStatus,
  onSaveWithComment,
  canAdminEditEvent,
  allowParticipantSelfEdit = false,
  team,
  isBulkUpdating = false,
  bulkSelectableStatuses = [],
  onBulkSelectStatus,
  tab = 'upcoming',
  onTabChange,
}: EventListTableProps) => {
  const {
    data: { user, gameVersion },
  } = usePageInfo()
  const [openDropdownEventId, setOpenDropdownEventId] = useState<number>()
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({})
  const [bulkDropdownOpen, setBulkDropdownOpen] = useState(false)
  const [raidReportsDialogEventId, setRaidReportsDialogEventId] = useState<
    number | undefined
  >(undefined)
  const [raidReportsDialogOpen, setRaidReportsDialogOpen] = useState(false)
  const wasBulkUpdating = useRef(false)
  const hasEvents = events.length > 0
  const isTeamScoped = !!team
  const shouldIncludeTeamColumn = !isTeamScoped
  const selectedEventIds = useMemo(
    () => Object.keys(rowSelection).map((id) => Number(id)),
    [rowSelection],
  )
  const selectedCount = selectedEventIds.length
  const canBulkEdit =
    !!user &&
    !!onBulkSelectStatus &&
    bulkSelectableStatuses.length > 0 &&
    tab !== 'past'
  const {
    data: raidReportsDialogEvent,
    isLoading: isRaidReportsDialogEventLoading,
  } = useTeamEvent(team?.id, raidReportsDialogEventId)
  const { data: raidReports = [], isLoading: isRaidReportsLoading } =
    useEventRaidReports(
      team?.id,
      raidReportsDialogEventId,
      raidReportsDialogOpen,
    )
  const { data: raidReportsMapResponse, isLoading: isRaidReportsMapLoading } =
    useTeamEventRaidReportsMap(team?.id, isTeamScoped)
  const raidReportsByEventId =
    raidReportsMapResponse?.raidReportsByEventId || {}

  useEffect(() => {
    if (wasBulkUpdating.current && !isBulkUpdating) {
      setRowSelection({})
    }
    wasBulkUpdating.current = isBulkUpdating
  }, [isBulkUpdating])

  const columns = useMemo<ColumnDef<wowaudit.EventReference>[]>(
    () => [
      eventTitleColumn(team) as ColumnDef<wowaudit.EventReference>,
      ...(shouldIncludeTeamColumn
        ? [
            {
              id: 'team',
              header: 'Team',
              cell: ({
                row,
              }: {
                row: { original: wowaudit.EventReference }
              }) => {
                const event = row.original
                const teamReference = (event as wowaudit.UserEventReference)
                  .teamReference
                return teamReference ? (
                  <div className='flex items-center gap-2'>
                    <BannerImage
                      faction={teamReference.ownerReference.faction}
                      fileName={teamReference.ownerReference.bannerFilename}
                      size='custom'
                      className='w-8 h-8 min-w-8 min-h-8'
                    />
                    <div className='min-w-0 leading-tight'>
                      <div className='truncate'>
                        {teamReference.ownerReference.name}
                      </div>
                      <div className='text-xs text-muted-foreground truncate'>
                        {teamReference.name}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className='text-muted-foreground italic'>Unknown</div>
                )
              },
            } as ColumnDef<wowaudit.EventReference>,
          ]
        : []),
      eventDateColumn() as ColumnDef<wowaudit.EventReference>,
      eventTimeColumn() as ColumnDef<wowaudit.EventReference>,
      {
        id: 'composition',
        header: 'Composition',
        cell: ({ row }) => {
          const event = row.original
          const presentParticipants = event.participantReferences.filter(
            (participant) =>
              presentParticipantStatuses.includes(participant.status),
          )
          const presentTanks = presentParticipants.filter(
            (participant) => participant.role === 'tank',
          ).length
          const presentHealers = presentParticipants.filter(
            (participant) => participant.role === 'heal',
          ).length
          const presentMelee = presentParticipants.filter(
            (participant) => participant.role === 'melee',
          ).length
          const presentRanged = presentParticipants.filter(
            (participant) => participant.role === 'ranged',
          ).length
          return (
            <div className='flex items-center gap-3'>
              <div className='flex items-center gap-1.5 w-10'>
                <CharacterRoleIcon role='tank' size='medium' />
                <span>{presentTanks}</span>
              </div>
              <div className='flex items-center gap-1.5 w-10'>
                <CharacterRoleIcon role='heal' size='medium' />
                <span>{presentHealers}</span>
              </div>
              <div className='flex items-center gap-1.5 w-10'>
                <CharacterRoleIcon role='melee' size='medium' />
                <span>{presentMelee}</span>
              </div>
              <div className='flex items-center gap-1.5 w-10'>
                <CharacterRoleIcon role='ranged' size='medium' />
                <span>{presentRanged}</span>
              </div>
              <div className='h-6 w-px bg-border' />
              <span className='flex items-center gap-1 font-normal text-muted-foreground text-xs'>
                <span
                  className={cn(
                    'font-semibold text-sm',
                    participantColor(event, gameVersion),
                  )}
                >
                  {presentParticipants.length}
                </span>
                <span>/</span>
                <span>{event.participantReferences.length}</span>
              </span>
            </div>
          )
        },
      },
      ...(isTeamScoped
        ? [
            {
              ...eventRaidReportsColumn({
                raidReportsByEventId,
                isLoading: isRaidReportsMapLoading,
                onOpen: (eventId) => {
                  setRaidReportsDialogEventId(eventId)
                  setRaidReportsDialogOpen(true)
                },
              }),
            } as ColumnDef<wowaudit.EventReference>,
          ]
        : []),
      ...(user
        ? [
            {
              id: 'signup',
              header: 'My status',
              cell: ({ row }) => {
                const event = row.original
                const participant = event.participantReferences.find(
                  (eventParticipant) =>
                    eventParticipant.characterReference.userId === userId,
                )
                const membership = membershipForEvent(event)
                const currentStatus = participant?.status ?? 'unknown'
                const canCreateSignup = !!membership
                const canEditParticipant =
                  event.status !== 'locked' &&
                  ((canAdminEditEvent && canAdminEditEvent(event)) ||
                    canCreateSignup ||
                    (allowParticipantSelfEdit && !!participant))
                const canEditStatus =
                  canEditParticipant && (!!participant || canCreateSignup)
                return (
                  <Card
                    className={cn(
                      'p-0 max-w-20',
                      canEditStatus
                        ? 'hover:bg-card transition-colors'
                        : 'border-transparent bg-transparent hover:bg-transparent',
                    )}
                  >
                    <EventParticipantStatusControl
                      main={membership}
                      participant={participant}
                      status={currentStatus}
                      canEditParticipant={canEditStatus}
                      selectableStatuses={selectableStatusesForEvent(event)}
                      isUpdatingStatus={
                        isUpdatingStatus && updatingEventId === event.id
                      }
                      open={openDropdownEventId === event.id}
                      iconSize='small'
                      onOpenChange={(open) =>
                        setOpenDropdownEventId(open ? event.id : undefined)
                      }
                      onSelect={(nextStatus) =>
                        onSelectStatus({
                          event,
                          participant,
                          membership,
                          nextStatus,
                        })
                      }
                      onSaveWithComment={
                        onSaveWithComment && participant
                          ? (nextStatus, comment) =>
                              onSaveWithComment({
                                event,
                                participant,
                                membership,
                                nextStatus,
                                comment,
                              })
                          : undefined
                      }
                      triggerClassName='focus:outline-none w-full h-8 flex items-center justify-center'
                      stopPropagation
                    />
                  </Card>
                )
              },
            } as ColumnDef<wowaudit.EventReference>,
          ]
        : []),
    ],
    [
      canAdminEditEvent,
      allowParticipantSelfEdit,
      bulkSelectableStatuses,
      shouldIncludeTeamColumn,
      isTeamScoped,
      isUpdatingStatus,
      membershipForEvent,
      onSelectStatus,
      onSaveWithComment,
      openDropdownEventId,
      onBulkSelectStatus,
      selectableStatusesForEvent,
      user,
      userId,
      raidReportsByEventId,
      isRaidReportsMapLoading,
    ],
  )

  return (
    <Card className='space-y-2 p-0'>
      {onTabChange || canBulkEdit || totalPages > 1 ? (
        <div className='flex items-center px-2 pt-2 gap-2 flex-wrap justify-end'>
          <div className={cn('flex items-center gap-2 ml-2 grow')}>
            {onTabChange ? (
              <Tabs
                value={tab}
                onValueChange={(v) => {
                  setRowSelection({})
                  onTabChange(v as 'upcoming' | 'past')
                }}
              >
                <TabsList>
                  <TabsTrigger value='upcoming'>Upcoming</TabsTrigger>
                  <TabsTrigger value='past'>Past</TabsTrigger>
                </TabsList>
              </Tabs>
            ) : null}

            {canBulkEdit ? (
              <div
                className={cn(
                  'flex items-center gap-2',
                  selectedCount === 0
                    ? 'opacity-0'
                    : 'opacity-100 transition-opacity',
                )}
              >
                <span>
                  {selectedCount} upcoming event{selectedCount !== 1 ? 's' : ''}{' '}
                  selected
                </span>
                <EventParticipantEditPopover
                  open={bulkDropdownOpen}
                  onOpenChange={setBulkDropdownOpen}
                  trigger={
                    <button
                      className='inline-flex h-7 items-center gap-1 rounded-md border border-border bg-card-nested hover:bg-accent px-2 text-xs disabled:opacity-50 disabled:pointer-events-none'
                      disabled={isBulkUpdating || selectedCount === 0}
                    >
                      {isBulkUpdating ? (
                        <Loader2 className='h-3.5 w-3.5 animate-spin' />
                      ) : null}
                      {isBulkUpdating ? 'Updating...' : 'Change status'}
                    </button>
                  }
                  selectableStatuses={bulkSelectableStatuses}
                  currentStatus='unknown'
                  isSaving={isBulkUpdating}
                  onSave={(nextStatus, comment) => {
                    onBulkSelectStatus?.(selectedEventIds, nextStatus, comment)
                    setBulkDropdownOpen(false)
                  }}
                  align='start'
                />
              </div>
            ) : null}
          </div>

          {totalPages > 1 ? (
            <TablePagination
              currentPage={page}
              totalPages={totalPages}
              onPageChange={onPageChange}
              showPreviousNext={true}
              disabled={isLoadingEvents}
            />
          ) : null}
        </div>
      ) : null}

      <div
        className={cn(
          'relative transition-[min-height] duration-200',
          isLoadingEvents && !hasEvents && 'min-h-[60rem]',
        )}
      >
        {isLoadingEvents && (
          <div className='absolute inset-0 z-20 flex items-center justify-center bg-card/60 pointer-events-auto cursor-wait' />
        )}
        <div
          className={cn(
            'transition-opacity duration-200',
            isLoadingEvents && !hasEvents
              ? 'opacity-0 pointer-events-none'
              : '',
          )}
        >
          <DataTable
            className='whitespace-nowrap'
            data={events}
            columns={columns}
            rowSelection={canBulkEdit ? rowSelection : undefined}
            onRowSelectionChange={canBulkEdit ? setRowSelection : undefined}
            selectionFilterFn={(event) =>
              !dayjs(event.endsAt).isBefore(dayjs())
            }
            getRowId={(event) => event.id.toString()}
            wrapperComponent={PlainWrapper}
            includeHeader={true}
            onRowClick={onRowClick}
            rowClassName={(event) =>
              cn(
                event.status === 'cancelled'
                  ? 'bg-destructive'
                  : event.optional
                  ? 'bg-info'
                  : '',
                event.status === 'cancelled'
                  ? 'hover:bg-destructive/80'
                  : event.optional
                  ? 'hover:bg-info/80'
                  : 'hover:bg-card-nested',
              )
            }
            emptyMessage={() =>
              isLoadingEvents ? 'Loading events...' : 'No events found'
            }
          />
        </div>
      </div>

      {totalPages > 1 ? (
        <div className='flex justify-end px-2 pb-2'>
          <TablePagination
            currentPage={page}
            totalPages={totalPages}
            onPageChange={onPageChange}
            showPreviousNext={true}
            disabled={isLoadingEvents}
          />
        </div>
      ) : null}
      {isTeamScoped ? (
        <EventRaidReportsDialog
          open={raidReportsDialogOpen}
          onOpenChange={(open) => {
            setRaidReportsDialogOpen(open)
            if (!open) {
              setRaidReportsDialogEventId(undefined)
            }
          }}
          reports={raidReports}
          event={raidReportsDialogEvent}
          isLoading={isRaidReportsLoading || isRaidReportsDialogEventLoading}
        />
      ) : null}
    </Card>
  )
}

export default EventListTable
